import { Button, Center, Group, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { Enums } from "@how/database";
import { semanticColor } from "@how/design-system";
import {
  buildPlatformHost,
  churchFromPath,
  isValidTenantSlug,
  parseHost,
  platformAddressFor,
  staffDoorUrl,
  type HostResolution,
  type TenantSurface,
} from "@how/tenancy";
import type { RoleKey } from "@how/types";
import { ConfirmActionModal, ErrorState, LoadingState, PermissionDenied } from "@how/ui";
import type { AppEnv } from "./env";
import { isAuthorizationError } from "./query-client";
import { useSession, useSupabase } from "./session";
import { STAFF_ELSEWHERE_SENTENCE, isStaffElsewhere } from "./staff-elsewhere";
import type { AppSupabaseClient } from "./supabase";

export interface ResolvedTenant {
  readonly tenantId: string;
  readonly slug: string;
  readonly surface: Enums<"tenant_surface">;
  readonly tenantStatus: Enums<"tenant_status">;
  /** The church's plan (ADR 0055). */
  readonly plan: Enums<"plan_key">;
  /** The hostname that resolved (the real one, or VITE_HOW_DEV_TENANT_HOST on localhost). */
  readonly hostname: string;
  /** `/<slug>` on the staff door, empty on a church's own host; prefix for absolute links built outside the router. */
  readonly basePath: string;
}

export type TenantState =
  | { status: "loading" }
  | { status: "resolved"; tenant: ResolvedTenant }
  /** Central hosts (dash.<base>, login.<base>, app.<base>, admin.<base>, or bare base): no tenant, the app shows its tenant picker or redirects. */
  | { status: "central"; app: Extract<HostResolution, { kind: "central" }>["app"] }
  | { status: "unresolved"; reason: string };

const TenantContext = createContext<TenantState>({ status: "loading" });

/**
 * Whether the page is on a church's own address (`dash.mychurch.com`), and HoW's base domain (ADR 0067 A2). Outside a
 * TenantProvider (the control plane) it is never a church host, so its sign-in page keeps today's link.
 */
interface HostInfo {
  readonly churchHost: boolean;
  readonly baseDomain: string | null;
}
const HostContext = createContext<HostInfo>({ churchHost: false, baseDomain: null });

/** A development machine's own address: localhost, 127.0.0.1, or any *.localhost name. */
export function isLocalHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost");
}

/** On localhost, resolve as if served on the configured dev host; everywhere else use the real hostname. */
export function effectiveHostname(env: AppEnv, hostname: string): string {
  return isLocalHostname(hostname) && env.VITE_HOW_DEV_TENANT_HOST !== undefined
    ? env.VITE_HOW_DEV_TENANT_HOST
    : hostname;
}

export interface TenantProviderProps {
  readonly env: AppEnv;
  /** The surface this app serves; a hostname that resolves to another surface is treated as unresolved. */
  readonly surface: TenantSurface;
  readonly children: ReactNode;
}

/**
 * Resolves the tenant from the hostname: parseHost classifies it, then the anon-callable resolve_tenant_host RPC
 * confirms it (and is the only path for custom domains). Nothing here authorizes; RLS and RPCs still decide.
 */
export function TenantProvider({ env, surface, children }: TenantProviderProps) {
  const supabase = useSupabase();
  const hostname = effectiveHostname(env, window.location.hostname);
  const parsed = useMemo(
    () => parseHost(hostname, env.VITE_HOW_PLATFORM_BASE_DOMAIN),
    [hostname, env],
  );
  // On the staff door the church is the first path segment (spec section 5); it is looked up through its platform
  // dashboard hostname, so resolve_tenant_host stays the one lookup. Read once: switching churches reloads the page.
  const door = parsed.kind === "central" && parsed.app === "staff_door";
  const pathChurch = useMemo(
    () => (door ? churchFromPath(window.location.pathname) : null),
    [door],
  );
  const lookupHost =
    pathChurch !== null
      ? buildPlatformHost(pathChurch.slug, "staff_dashboard", env.VITE_HOW_PLATFORM_BASE_DOMAIN)
      : hostname;
  const lookup = parsed.kind === "platform" || parsed.kind === "custom" || pathChurch !== null;
  const query = useQuery({
    queryKey: ["tenant-host", lookupHost],
    enabled: lookup,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ResolvedTenant | null> => {
      const { data, error } = await supabase.rpc("resolve_tenant_host", { p_hostname: lookupHost });
      if (error) throw error;
      const row = data[0];
      if (row === undefined) return null;
      return {
        tenantId: row.tenant_id,
        slug: row.slug,
        surface: row.surface,
        tenantStatus: row.tenant_status,
        plan: row.plan,
        hostname: lookupHost,
        basePath: pathChurch !== null ? `/${pathChurch.slug}` : "",
      };
    },
  });

  const state: TenantState = !lookup
    ? parsed.kind === "central"
      ? { status: "central", app: parsed.app }
      : { status: "unresolved", reason: parsed.reason }
    : query.isPending
      ? { status: "loading" }
      : query.isError
        ? { status: "unresolved", reason: "lookup failed" }
        : query.data === null
          ? { status: "unresolved", reason: "no active tenant for this hostname" }
          : query.data.surface !== surface
            ? { status: "unresolved", reason: "hostname belongs to another surface" }
            : { status: "resolved", tenant: query.data };
  // A church's own address is any host outside HoW's domain (parseHost). A development machine standing in for one
  // (VITE_HOW_DEV_TENANT_HOST) keeps today's links: its emails could not reach localhost anyway.
  const hostInfo = useMemo<HostInfo>(
    () => ({
      churchHost: parsed.kind === "custom" && !isLocalHostname(window.location.hostname),
      baseDomain: env.VITE_HOW_PLATFORM_BASE_DOMAIN,
    }),
    [parsed, env],
  );
  return (
    <HostContext.Provider value={hostInfo}>
      <TenantContext.Provider value={state}>{children}</TenantContext.Provider>
    </HostContext.Provider>
  );
}

export function useTenant(): TenantState {
  return useContext(TenantContext);
}

/** The staff door's `/<slug>` for the resolved church, or "" on a church's own host. */
export function useTenantBasePath(): string {
  const t = useTenant();
  return t.status === "resolved" ? t.tenant.basePath : "";
}

/**
 * Whether this page is on the church's own address rather than HoW's (spec section 7). Decided from the host alone, so
 * it is true before the church is resolved; pair it with a resolved tenant.
 */
export function useOnChurchHost(): boolean {
  return useContext(HostContext).churchHost;
}

export interface EmailLinkTarget {
  /** Where the emailed sign-in link signs the person in. */
  readonly redirectTo: string;
  /** HoW's address without the scheme, shown under the button, when the link opens away from this address. */
  readonly opensAt: string | null;
}

/**
 * Where "Email me a sign-in link" sends the person (spec section 8, R8, ADR 0067 A3). Off a church's own address,
 * this page's origin and the church's base path, as before. On it, HoW's address for the church
 * (`dash.<base>/<slug>/`, `<slug>.members.<base>/`): the church's address is not in Supabase Auth's allow-list, and
 * the implicit flow lets the link sign in at HoW's. Null, and nothing is sent, until HoW's address is known.
 */
export function emailLinkTarget(input: {
  readonly origin: string;
  readonly churchHost: boolean;
  readonly baseDomain: string | null;
  readonly tenant: TenantState;
}): EmailLinkTarget | null {
  const { tenant } = input;
  const basePath = tenant.status === "resolved" ? tenant.tenant.basePath : "";
  if (!input.churchHost) return { redirectTo: `${input.origin}${basePath}/`, opensAt: null };
  if (tenant.status !== "resolved" || input.baseDomain === null) return null;
  const { slug, surface } = tenant.tenant;
  if (surface !== "staff_dashboard" && surface !== "member_portal") return null;
  if (!isValidTenantSlug(slug)) return null;
  const address = platformAddressFor({ slug, surface }, input.baseDomain);
  return { redirectTo: `${address}/`, opensAt: address.slice("https://".length) };
}

export function useEmailLinkTarget(): EmailLinkTarget | null {
  const host = useContext(HostContext);
  const tenant = useTenant();
  return emailLinkTarget({
    origin: window.location.origin,
    churchHost: host.churchHost,
    baseDomain: host.baseDomain,
    tenant,
  });
}

/** Renders children only for a resolved tenant; otherwise the "unknown church" state. */
export function RequireTenant({ children }: { children: ReactNode }) {
  const tenant = useTenant();
  if (tenant.status === "loading") return <LoadingState label="Finding your church" />;
  if (tenant.status === "unresolved" || tenant.status === "central") {
    return (
      <ErrorState
        title="We could not find this church"
        message={
          tenant.status === "central"
            ? "This address is the church picker. Sign in there to choose your church, or open your church's own address."
            : "Check the address you used. If your church gave you a link, open that link again."
        }
      />
    );
  }
  return <>{children}</>;
}

export type MembershipState = Enums<"membership_status"> | null;

export interface MembershipRecord {
  /** The membership row's id: declining an invitation names it (set_membership_status). */
  readonly id: string;
  readonly status: Enums<"membership_status">;
  readonly roles: readonly { readonly key: RoleKey; readonly isPrivileged: boolean }[];
}

/**
 * The caller's own membership row in a tenant with its roles, in one request (#286): the status gates the tenant
 * surfaces and the roles feed the tenant MFA rule. The embedded rows pass the same self-read policies as reading each
 * table on its own. Null when there is no membership.
 */
export function membershipQuery(supabase: AppSupabaseClient, tenantId: string, userId: string) {
  return queryOptions({
    queryKey: ["membership", tenantId, userId],
    queryFn: async (): Promise<MembershipRecord | null> => {
      const { data, error } = await supabase
        .from("tenant_memberships")
        .select("id, status, tenant_membership_roles(roles(key, is_privileged))")
        .eq("tenant_id", tenantId)
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      if (data === null) return null;
      return {
        id: data.id,
        status: data.status,
        roles: data.tenant_membership_roles.flatMap((link) => {
          // Typed as always there, but RLS can hide a church's own role from an invited member: it embeds as null.
          const role = link.roles as { key: string; is_privileged: boolean } | null;
          return role === null
            ? []
            : [{ key: role.key as RoleKey, isPrivileged: role.is_privileged }];
        }),
      };
    },
  });
}

/** The caller's membership status in the resolved tenant (self-read policy); null when there is none. */
export function useMembership() {
  const supabase = useSupabase();
  const session = useSession();
  const tenant = useTenant();
  const userId = session.status === "signed_in" ? session.session.user.id : null;
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : null;
  return useQuery({
    ...membershipQuery(supabase, tenantId ?? "", userId ?? ""),
    enabled: userId !== null && tenantId !== null,
    select: (record): MembershipState => record?.status ?? null,
  });
}

/**
 * Where a person lands after declining a staff invitation: the staff door's own `/` (their other churches, or the
 * picker's empty state). Already on the door that is this origin, so localhost stays local; on a church's own address
 * or an old dashboard host it is the door's absolute address.
 */
export function declinedLanding(basePath: string, baseDomain: string | null): string {
  if (basePath !== "" || baseDomain === null) return "/";
  return staffDoorUrl(baseDomain);
}

/**
 * An invited member accepts here, from the link in their invitation email or by signing in: accept_tenant_invitation
 * turns their own invitation for this church into an active membership (#136). Nothing is accepted without the click.
 * On the staff dashboard they may decline it instead (ADR 0068 follow-up): a stale staff invitation counts toward the
 * one-staff-church rule, so the person can clear it themselves. set_membership_status already lets a person remove
 * their own membership (leaving); declining is that, on an invited one, audited as membership.status_changed.
 */
function AcceptInvitation({ tenantId, surface }: { tenantId: string; surface: TenantSurface }) {
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const session = useSession();
  const host = useContext(HostContext);
  const basePath = useTenantBasePath();
  const userId = session.status === "signed_in" ? session.session.user.id : "";
  const record = useQuery({
    ...membershipQuery(supabase, tenantId, userId),
    enabled: userId !== "",
  });
  const [confirming, setConfirming] = useState(false);
  // The church's name, so the person knows what they are accepting (#177 review). An invited member may read their
  // church's row (tenants_select admits "invited"); until it arrives, or if it cannot be read, it says "this church".
  const church = useQuery({
    queryKey: ["invited-church", tenantId],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("tenants")
        .select("name")
        .eq("id", tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data as { name?: string } | null)?.name ?? null;
    },
  });
  const churchName = church.data ?? null;
  const accept = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("accept_tenant_invitation", { p_tenant_id: tenantId });
      // P0002: the invitation was accepted already (another tab, a second click); the membership query says what is true.
      if (error && error.code !== "P0002") throw error;
    },
    // Everything read before accepting was read as a non-member (the modules, the settings, each permission check
    // started with them at start-up, #286), so all of it is asked again, not only the membership.
    onSettled: () => queryClient.invalidateQueries(),
  });
  const decline = useMutation({
    mutationFn: async () => {
      const membershipId = record.data?.id;
      if (membershipId === undefined) throw new Error("membership not loaded");
      const { error } = await supabase.rpc("set_membership_status", {
        p_membership_id: membershipId,
        p_status: "removed",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setConfirming(false);
      notifications.show({
        color: semanticColor("success"),
        title: "Invitation declined",
        message: `You declined the invitation to ${churchName ?? "this church"}.`,
      });
      // A full page load: nothing read for this church survives.
      window.location.assign(declinedLanding(basePath, host.baseDomain));
    },
    onError: () => {
      setConfirming(false);
    },
  });
  const canDecline = surface === "staff_dashboard";
  return (
    <Center py="xl" px="md">
      <Stack align="center" gap="sm" maw={440} w="100%" ta="center">
        <Title order={3}>You are invited to {churchName ?? "this church"}</Title>
        <Text c="dimmed" size="sm">
          {canDecline
            ? "Accept the invitation to start using it with this account, or decline it if you don't want access."
            : "Accept the invitation to start using it with this account."}
        </Text>
        {accept.isError ? (
          <Text c="red" size="sm" role="alert">
            {isStaffElsewhere(accept.error)
              ? STAFF_ELSEWHERE_SENTENCE
              : "The invitation could not be accepted. Try again, or ask the church for a new one."}
          </Text>
        ) : null}
        {decline.isError ? (
          <Text c="red" size="sm" role="alert">
            The invitation could not be declined. Try again, or ask the church to cancel it.
          </Text>
        ) : null}
        <Group justify="center" gap="sm" wrap="wrap">
          <Button
            loading={accept.isPending}
            disabled={decline.isPending}
            onClick={() => {
              accept.mutate();
            }}
          >
            Accept invitation
          </Button>
          {canDecline ? (
            <Button
              variant="default"
              disabled={accept.isPending || record.data === undefined}
              onClick={() => {
                decline.reset();
                setConfirming(true);
              }}
            >
              Decline invitation
            </Button>
          ) : null}
        </Group>
      </Stack>
      {canDecline ? (
        <ConfirmActionModal
          opened={confirming}
          onClose={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            decline.mutate();
          }}
          title="Decline invitation"
          message={`Decline the invitation to ${churchName ?? "this church"}? You won't have access there unless they invite you again.`}
          confirmLabel="Decline invitation"
          danger
          loading={decline.isPending}
        />
      ) : null}
    </Center>
  );
}

/**
 * A signed-in user without an active membership in this tenant sees PermissionDenied with a sign-out action; one who
 * was invited is offered the invitation instead.
 */
export function RequireMembership({ children }: { children: ReactNode }) {
  const supabase = useSupabase();
  const tenant = useTenant();
  const membership = useMembership();
  if (membership.isPending) return <LoadingState label="Checking your membership" />;
  if (membership.isError) {
    return isAuthorizationError(membership.error) ? (
      <PermissionDenied />
    ) : (
      <ErrorState
        message="We could not check your membership."
        onRetry={() => {
          void membership.refetch();
        }}
      />
    );
  }
  if (membership.data === "invited" && tenant.status === "resolved") {
    return <AcceptInvitation tenantId={tenant.tenant.tenantId} surface={tenant.tenant.surface} />;
  }
  if (membership.data !== "active") {
    return (
      <PermissionDenied
        title="You do not have access to this church"
        message={
          membership.data === null
            ? "Your account is signed in but is not a member here. Ask a church administrator for an invitation."
            : `Your membership is ${membership.data}. Ask a church administrator to restore it.`
        }
        action={
          <Button
            variant="light"
            onClick={() => {
              void supabase.auth.signOut();
            }}
          >
            Sign out
          </Button>
        }
      />
    );
  }
  return <>{children}</>;
}
